import {
  ApprovalAssigneeType,
  ApprovalParallelPolicy,
  ApprovalStepScopeType,
  groupApprovalSteps,
  type ApprovalFlowStep,
  type ApprovalFlowStepInput,
} from "./approval-flows.types";

/**
 * Akış tasarım ekranının çalışma modeli.
 *
 * Sunucu adımları DÜZ gönderiyor (paralel kademe = aynı `stepOrder`'a sahip
 * birden fazla satır) ve gönderim biçimi iki türlü (tek dallı kısa biçim ya da
 * `branches`). Ekranın her yerinde bu ikiliğe bakmamak için form tek bir biçimle
 * çalışır: her kademe her zaman en az bir dal taşır.
 */

/** Kademenin tek dalı — onaycı ve kapsam bu seviyededir. */
export interface BuilderBranch {
  title: string;
  description: string;
  assigneeType: ApprovalAssigneeType;
  roleIds: string[];
  userId?: string;
  scopeType: ApprovalStepScopeType;
  /**
   * Talebi görebilecek onaycı yoksa dal atlanır. Müdürlüğe bağlı olmayan
   * birimlerde (şeflik, servis, amirlik) "müdür onayı" adımı böyle geçilir.
   */
  skipWhenNoApprover: boolean;
  /**
   * Sunucudan gelen "bu dalı kaç kişi onaylayabilir" sayısı. Yeni eklenen
   * dallarda undefined'dır (henüz kaydedilmedi). 0 ise kart uyarı gösterir.
   */
  assigneeUserCount?: number;
}

/**
 * Bir onay kademesi.
 *
 * Kademenin ayrı bir başlığı YOKTUR: tek dallı kademede kartın "Adım Adı" alanı
 * o dalın başlığıdır, çok dallı kademede her dal kendi başlığını taşır ve
 * sunucuya kademe başlığı olarak ilk dalınki gönderilir. Ayrı bir kademe
 * başlığı tutulsaydı sunucudan geri okunamaz (o alan hiçbir satırda saklanmıyor)
 * ve kullanıcı her yüklemede yazdığını kaybederdi.
 */
export interface BuilderStep {
  parallelPolicy: ApprovalParallelPolicy;
  branches: BuilderBranch[];
}

/** Yeni dal: rol onaycısı, talep sahibinin başkanlığıyla sınırlı. */
export const emptyBranch = (): BuilderBranch => ({
  title: "",
  description: "",
  assigneeType: ApprovalAssigneeType.ROLE,
  roleIds: [],
  // Varsayılan kapsam KÖK BİRİM (başkanlık / işletme müdürlüğü). Eskiden
  // "kendi birimi (müdürlük)" idi; onaycılar HR'da başkanlığın kendisine değil
  // altındaki bir müdürlüğe bağlı olduğu için kardeş müdürlüklerin talepleri
  // onlara hiç düşmüyor, adım sessizce kilitleniyordu (bkz. "eğitim
  // sorumlusuna onay düşmüyor"). Müdürlük kapsamı hâlâ seçilebilir — ama
  // bilinçli bir tercih olmalı, varsayılan değil.
  scopeType: ApprovalStepScopeType.REQUESTER_ROOT_DEPARTMENT,
  skipWhenNoApprover: false,
});

/** Yeni kademe: tek dallı, ANY politikalı. */
export const emptyStep = (): BuilderStep => ({
  parallelPolicy: ApprovalParallelPolicy.ANY,
  branches: [emptyBranch()],
});

/** Sunucudan gelen düz dal listesini forma çevirir. */
export const toBuilderSteps = (steps: ApprovalFlowStep[]): BuilderStep[] => {
  const groups = groupApprovalSteps(steps);
  if (groups.length === 0) return [emptyStep()];

  return groups.map(({ branches }) => ({
    // Politika kademenin özelliği; tüm dallar aynı değeri taşır.
    parallelPolicy: branches[0]?.parallelPolicy ?? ApprovalParallelPolicy.ANY,
    branches: branches.map((branch) => ({
      title: branch.title,
      description: branch.description ?? "",
      assigneeType: branch.assigneeType,
      // `roles` çoklu rol alanı; eski kayıtlarda tekil `role` dolu gelebildiği
      // için ona düşülür.
      roleIds: branch.roles?.length ? branch.roles.map((r) => r.id) : branch.role ? [branch.role.id] : [],
      userId: branch.user?.id,
      scopeType: branch.scopeType,
      skipWhenNoApprover: branch.skipWhenNoApprover ?? false,
      assigneeUserCount: branch.assigneeUserCount,
    })),
  }));
};

/**
 * Formu gönderim biçimine çevirir.
 *
 * Tek dallı kademe kısa biçimde gider (paralel kademe eklenmeden önceki gövdenin
 * aynısı); çok dallı kademe `branches` ile gider ve kademenin kendi atama
 * alanları boş kalır — sunucu ikisini birden kabul etmiyor.
 */
export const toStepPayload = (step: BuilderStep): ApprovalFlowStepInput => {
  const assigneeFields = (branch: BuilderBranch) => ({
    assigneeType: branch.assigneeType,
    // assigneeType'a uymayan alanı göndermiyoruz; backend ikisinin birden dolu
    // olmasını reddediyor.
    roleIds: branch.assigneeType === ApprovalAssigneeType.ROLE ? branch.roleIds : undefined,
    userId: branch.assigneeType === ApprovalAssigneeType.USER ? branch.userId : undefined,
    scopeType: branch.scopeType,
    skipWhenNoApprover: branch.skipWhenNoApprover,
  });

  const [first] = step.branches;
  if (step.branches.length === 1) {
    return {
      title: first.title.trim(),
      description: first.description.trim() || undefined,
      ...assigneeFields(first),
    };
  }

  return {
    // Kademe başlığı: ilk dalınki. Sunucu bunu yalnızca başlıksız dallar için
    // yedek olarak kullanıyor, dallarımızın hepsi kendi başlığını taşıyor.
    title: first.title.trim(),
    parallelPolicy: step.parallelPolicy,
    branches: step.branches.map((branch) => ({
      title: branch.title.trim(),
      description: branch.description.trim() || undefined,
      ...assigneeFields(branch),
    })),
  };
};

/**
 * Tasarımı karşılaştırılabilir bir anahtara çevirir.
 *
 * Kaydetmede `steps` alanını yalnızca tasarım gerçekten değiştiyse gönderiyoruz:
 * backend adım listesi geldiğinde karar bekleyen talep var mı diye bakıyor, bu
 * yüzden sadece başlık değiştirmek ya da akışı pasife almak bile 409'a
 * takılıyordu.
 */
export const stepsKey = (steps: BuilderStep[]): string =>
  JSON.stringify(
    steps.map((step) => ({
      // Politika yalnızca çok dallı kademede anlamlı; tek dallıda ANY/ALL aynı
      // şeye çıktığı için farkı "tasarım değişti" saymıyoruz (backend de saymaz).
      parallelPolicy: step.branches.length > 1 ? step.parallelPolicy : ApprovalParallelPolicy.ANY,
      branches: step.branches.map((branch) => ({
        title: branch.title.trim(),
        description: branch.description.trim(),
        assigneeType: branch.assigneeType,
        // Rol kümesi sırasız: aynı roller farklı sırada seçilirse tasarım
        // "değişti" sayılmamalı, yoksa dallar boşuna yeniden yazılır.
        roleIds: branch.assigneeType === ApprovalAssigneeType.ROLE ? [...branch.roleIds].sort() : [],
        userId: (branch.assigneeType === ApprovalAssigneeType.USER ? branch.userId : undefined) ?? "",
        scopeType: branch.scopeType,
        skipWhenNoApprover: branch.skipWhenNoApprover,
      })),
    })),
  );

/**
 * Kaydetmeden önce zorunlu alan kontrolü — backend de doğruluyor, bu erken geri
 * bildirim. Hata mesajı 1 tabanlı kademe (ve gerekirse dal) sırasını taşır.
 */
export const validateSteps = (steps: BuilderStep[]): string | null => {
  if (steps.length === 0) return "Akış en az bir adım içermelidir.";

  for (const [i, step] of steps.entries()) {
    const label = (branchIndex: number) =>
      step.branches.length > 1 ? `${i + 1}. adımın ${branchIndex + 1}. dalında` : `${i + 1}. adımda`;

    const seen = new Set<string>();
    for (const [j, branch] of step.branches.entries()) {
      if (!branch.title.trim()) return `${label(j)} başlık boş olamaz.`;
      if (branch.assigneeType === ApprovalAssigneeType.ROLE && branch.roleIds.length === 0) {
        return `${label(j)} en az bir rol seçilmelidir.`;
      }
      if (branch.assigneeType === ApprovalAssigneeType.USER && !branch.userId) {
        return `${label(j)} kişi seçilmelidir.`;
      }

      // Backend de reddediyor: ANY'de ikinci dal hiç sıra almaz, ALL'da aynı
      // kişiden iki onay beklenir ve kademe kilitlenir.
      const key =
        branch.assigneeType === ApprovalAssigneeType.ROLE
          ? `role:${[...branch.roleIds].sort().join(",")}`
          : `user:${branch.userId}`;
      if (seen.has(key)) return `${i + 1}. adımda aynı onaycı birden fazla dalda kullanılamaz.`;
      seen.add(key);
    }
  }
  return null;
};
